import React, { useState, useEffect } from 'react';
import { Paper } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import useStyle from './useStyle';
import newButton from '@/assets/img/iconos/newButton.svg';
import searchIcon from '@/assets/img/iconos/searchIcon.svg';
import ActionForm from './components/ActionForm/ActionForm';
import { CustomModal, Loading, Toaster } from '@/components';
import CsvReader from './components/CsvReader/CsvReader';
import BulkLoadPreview from './components/BulkLoadPreview/BulkLoadPreview';
import getAllCommercialCategoryService from '@/services/commercialCategory/getAllCommercialCategoryService';
import postBatchCommercialCategoriesService from '@/services/commercialCategory/postBatchCommercialCategoriesService';
import { getAllRubros, createBatchRubro } from '@/services';
import { errorAlert, successAlert, defaultData } from './constants';
import CommercialCategoriesTable from './components/DataTable/CommercialCategoriesTable';

const CommercialCategoryScreen = () => {
  const classes = useStyle();
  const [alert, setAlert] = useState({ status: false, type: '', message: '' });
  const [showBulkPreview, setShowBulkPreview] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showDuplicatedMccDialog, setShowDuplicatedMccDialog] = useState(false);
  const [duplicatedMcc, setDuplicatedMcc] = useState<any[]>([]);
  const [enteredFilter, setEnteredFilter] = useState('');
  const [defaultValues, setDefaultValues] = useState(defaultData);
  const [errorMessage, setErrorMessage] = useState('');
  const [comercialCategoryList, setComercialCategoryList] = useState<{ mcc: string; [key: string]: any }[]>([]);
  const [ comercialCategoryListForCsvReader, setComercialCategoryListForCsvReader ] = useState({});
  const [sectorsList, setSectorsList] = useState<Record<string, { id: string; description: string }>>({});
  const [bulkData, setBulkData] = useState<{ 
    businessCategory: string; 
    description: string; 
    mcc: string; 
    itbis: string; 
    visible: boolean; 
    classification: string; 
  }[]>([]);
  const [popUpAction, setPopUpAction] = useState({ open: false, title: '', data: null });
  const [loading, setLoading] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);

  const fetchAllCategories = async () => {
    setLoading(true);
    const res = await getAllCommercialCategoryService();
    if (!res) {
      setAlert(errorAlert);
      setLoading(false);
      return;
    }
    const { data: commercialCategory, status } = res;
    if (status !== 200) {
      setAlert(errorAlert);
    } else {
      setComercialCategoryList(commercialCategory);
    }

    setLoading(false);
  };

  const prepareCommercialCategoriesForCsvReader = () => {
    const hashTable: Record<string, typeof comercialCategoryList[0]> = {};

    comercialCategoryList.forEach((item) => {
      const mcc = item.mcc;
      hashTable[`${mcc}`] = item;
    });

    setComercialCategoryListForCsvReader(hashTable);
  };

  const fetchAllRubros = async () => {
    const res = await getAllRubros();
    if (!res) {
      setAlert(errorAlert);
      return;
    }

    const { data: sectorsData, status }: { data: {id: string, description: string }[]; status: number } = res;
    const sectorsHashTable: Record<string, { id: string; description: string }> = {};

    if (status !== 200) {
      setAlert(errorAlert);
    } else {
      sectorsData.forEach((item) => {
        const { id, description } = item;
        sectorsHashTable[`${description}`] = { id, description };
      });

      setSectorsList(sectorsHashTable);
    }

    return sectorsHashTable;
  };

  const saveHandler = async () => {  
    setLoading(true);
    try {
      const missingCategories = bulkData
        .filter(
          (data) => !Object.keys(sectorsList)
            .some((key) => key.toUpperCase() === data.businessCategory.toUpperCase())
        )
        .map((data) => ({ id: '', description: data.businessCategory.toUpperCase(), enabled: true }));
      let batchPayload = [];
      if (missingCategories.length > 0) {
        const res = await createBatchRubro(missingCategories);

        if (!res) {
          setAlert(errorAlert);
          return;
        }
        const { status } = res;

        if (status === 201) {
          const resR = await getAllRubros();
          if (!resR) {
            setAlert(errorAlert);
            return;
          }

          const { data: allRubrosData }: { data: Record<string, { id: string }> } = resR;

          batchPayload = bulkData.map((data) => ({
            detail_name_es: data.description,
            enabled: true,
            mcc: data.mcc,
            itbis: data.itbis === 'SI',
            justBackOffice: !data.visible,
            rubro: data.businessCategory.toUpperCase(),
            rubro_id: allRubrosData[data.businessCategory.toUpperCase()]?.id,
            classification: data.classification
          }));
        } else {
          throw new Error('Error creating business category batch');
        }
      } else {
        batchPayload = bulkData.map((data) => ({
          detail_name_es: data.description,
          enabled: true,
          mcc: data.mcc,
          itbis: data.itbis === 'SI',
          justBackOffice: !data.visible,
          rubro: data.businessCategory.toUpperCase(),
          rubro_id: sectorsList[data.businessCategory.toUpperCase()].id,
          classification: data.classification,
        }));
      }

      
      const response = await postBatchCommercialCategoriesService(batchPayload);
      if (!response) {
        setAlert(errorAlert);
        return;
      }
      const { status } = response;

      if (status === 200 || status === 201) {
        setComercialCategoryList((prev) => ([ ...prev, ...batchPayload ]));
        setShowBulkPreview(false);
        setAlert(successAlert);
      } else {
        setAlert(errorAlert);
      }
    } catch (error) {
      setAlert(errorAlert);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCategories();
    fetchAllRubros();
  }, []);

  const createNewCommercialCategory = () => {
    setDefaultValues(defaultData);
    setPopUpAction({
      open: true,
      title: 'Crear nueva categoría',
      data: null,
    });
    setUpdateMode(false);
  };

  return (
    <Paper className={classes.paper}>
      {loading && <Loading />}

      <div className={classes.root}>
        <div>
          <TextField
            className={classes.searchTextField}
            value={enteredFilter}
            onChange={(e) => setEnteredFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img
                    src={searchIcon}
                    alt="searchIcon"
                    style={{ paddingLeft: '10px' }}
                  />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
            placeholder="Buscar"
          />
          <Tooltip
            title="Crear nueva categoría"
            aria-label="Crear nueva categoría"
          >
            <IconButton
              onClick={createNewCommercialCategory}
              style={{ fontSize: '20px', float: 'right' }}
              edge="end"
              size="small"
              classes={{ disabled: classes.disabled }}
            >
              <img src={newButton} alt="newButton" />
            </IconButton>
          </Tooltip>

          <CsvReader
            sectorsList={sectorsList || {}}
            setData={setBulkData}
            setErrorMessage={setErrorMessage}
            setShowBulkPreview={setShowBulkPreview}
            setShowErrorDialog={setShowErrorDialog}
            comercialCategoryList={comercialCategoryListForCsvReader}
            setDuplicatedMcc={setDuplicatedMcc}
            setShowDuplicatedMccDialog={setShowDuplicatedMccDialog}
            onClick={prepareCommercialCategoriesForCsvReader}
          />
        </div>

        <CommercialCategoriesTable
          commercialCategories={comercialCategoryList}
          setDefaultValues={setDefaultValues}
          enteredFilter={enteredFilter}
          setPopUpAction={setPopUpAction}
          setUpdateMode={setUpdateMode}
        />
      </div>

      <ActionForm
        open={popUpAction.open}
        data={popUpAction.data}
        sectorsList={sectorsList}
        title={popUpAction.title}
        defaultValues={defaultValues}
        getCommercialCategories={fetchAllCategories}
        comercialCategoryList={comercialCategoryList}
        updateMode={updateMode}
        fetchAllRubros={fetchAllRubros}
        onClose={() => {
          setPopUpAction({ open: false, title: '', data: null });
          fetchAllCategories();
        }}
      />

      <BulkLoadPreview
        open={showBulkPreview}
        data={bulkData}
        sectorsList={sectorsList}
        setData={setBulkData}
        duplicatedMcc={duplicatedMcc}
        setMainAlert={setAlert}
        onClose={() => setShowBulkPreview(false)}
        onSave={saveHandler}
        loading={loading}
      />

      <CustomModal
        firstButtonAction={() => setShowErrorDialog(false)}
        secondButtonAction={() => {}}
        isOpen={showErrorDialog}
        setIsOpen={setShowErrorDialog}
        title="Error al cargar documento"
        firstButtonLabel="Aceptar"
        secondButtonLabel=""
        text={''}
      >
        <p style={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</p>
      </CustomModal>

      <CustomModal
        firstButtonAction={() => setShowBulkPreview(true)}
        isOpen={showDuplicatedMccDialog && !errorMessage}
        setIsOpen={setShowDuplicatedMccDialog}
        title="¡Categorías existentes!"
        firstButtonLabel="Continuar"
        secondButtonLabel="Detener"
        secondButtonAction={() => setShowDuplicatedMccDialog(false)}
        text={''}
      >
        <p>Algunas categorías que intentas agregar ya existen:</p>
        {duplicatedMcc.map((category: any) => (
          <p key={category.mcc}>
            {`${category.mcc} - ${category.description} - {category.businessCategory}`}
          </p>
        ))}
      </CustomModal>

      <Toaster
        show={alert.status}
        type={alert.type as any}
        text={alert.message}
        onClose={() => setAlert({ ...alert, status: false })}
      />
    </Paper>
  );
};

export default CommercialCategoryScreen;
